import { Dispatch, MutableRefObject, SetStateAction } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { iGlobalContext } from "../globalContext/interface";
import { iHistoryContext } from "../historyContext/interface";
import { useSocketContext } from "../socketContext/SocketContext";
import { usePocContext } from "../pocContext/pocContext";
import { ChatRequest, MaskedRequest, TypedTurn, voiceBus } from "../../services/voice/voiceBus";
import { Exchange } from "../../services/voice/liveTranscript";
import { TranscriptCleaner } from "../../services/voice/transcriptCleaner";
import { VoiceFrame } from "../../services/voice/voiceFrames";
import { TranscriptBackup } from "../../services/voice/transcriptBackup";
import type { VoiceStatus, Turn, VoiceSession } from "./types";

export interface VoiceLive {
  state: iGlobalContext;
  poc: ReturnType<typeof usePocContext>;
  handleOpenSocketConnection: ReturnType<typeof useSocketContext>["handleOpenSocketConnection"];
  history: iHistoryContext;
  location: ReturnType<typeof useLocation>;
  navigate: ReturnType<typeof useNavigate>;
}

export interface DeferredExchange {
  user: string;
  reply: string;
  liveSessionId: string | null;
  shownId: string | null;
  session: VoiceSession;
  exchange: Exchange;
}

export interface OpenedFrom {
  session: VoiceSession | null;
  firstScreen: string;
  screen: string;
  path: string;
}

export interface VoiceHandlers {
  onLiveEvent: (session: VoiceSession, event: any) => void;
  onLost: (session: VoiceSession) => void;
  requestOpened: (request: ChatRequest) => void;
  requestFailed: (request: ChatRequest) => void;
  requestClosed: (request: ChatRequest) => void;
  requestMasked: (request: MaskedRequest) => void;
  voiceFrame: (frame: VoiceFrame, replyKind: string | null) => void;
  typedTurn: (turn: TypedTurn) => void;
  sendChip: (text: string) => void;
  checkoutNote: (text: string) => void;
  tick: () => void;
  flushDeferred: () => void;
  startVoice: () => void;
  endVoice: (reason: string, remote?: boolean) => void;
  noteUserStop: () => void;
  toggleMute: () => void;
  onCleaned: (session: VoiceSession) => void;
  keepEmptied: (empty: boolean) => boolean;
}

export interface VoiceRuntime {
  live: MutableRefObject<VoiceLive>;
  sessionRef: MutableRefObject<VoiceSession | null>;
  turnRef: MutableRefObject<Turn | null>;
  chatIdRef: MutableRefObject<string>;
  deferredRef: MutableRefObject<DeferredExchange[]>;
  lastFlushRef: MutableRefObject<number>;
  recoveredRef: MutableRefObject<boolean>;
  repliesRef: MutableRefObject<Array<{ turn: Turn; cleaner: TranscriptCleaner }>>;
  emptiedRef: MutableRefObject<VoiceSession | null>;
  openedFromRef: MutableRefObject<OpenedFrom | null>;
  mutedRef: MutableRefObject<boolean>;
  backupRef: MutableRefObject<TranscriptBackup | null>;
  handlers: MutableRefObject<VoiceHandlers>;
  voiceEnabled: boolean;
  setVoiceStatus: Dispatch<SetStateAction<VoiceStatus>>;
  setVoiceRequestOpen: Dispatch<SetStateAction<boolean>>;
  setMuted: Dispatch<SetStateAction<boolean>>;
}

export const VOICE_EXCHANGE = "voiceExchange";

export const voiceLog = (step: string, payload: Record<string, unknown> = {}) => {
  console.log(`[voice] ${step}`, JSON.stringify(payload));
};

export const now = () => Date.now();

export const voiceMark = (session: VoiceSession, step: string, payload: Record<string, unknown> = {}) =>
  voiceLog(step, { ...payload, ms: now() - session.tapAt });

export const isRequestRunning = () => {
  const request = voiceBus.activeRequest();
  return !!request && request.requestType !== VOICE_EXCHANGE;
};

export const sendAppend = (
  session: VoiceSession,
  type: "session.commentary.append" | "session.thinking.append",
  delegationId: string | null,
  content: string,
): string | null => {
  const channel = session.peer?.channel;
  if (!channel || channel.readyState !== "open" || session.startedAt === null) return null;
  session.events += 1;
  const eventId = `zv_${session.events}`;
  channel.send(JSON.stringify({ type, event_id: eventId, delegation_id: delegationId, content }));
  return eventId;
};
